import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Car, MapPin, User, Wrench } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatInTimeZone } from 'date-fns-tz';
import { readShopOccupiedSpotIds } from '@/lib/location/api';
import { checkInSpotOptions } from '@/lib/location/availableSpots';

/**
 * Confirm Vehicle Check-In dialog.
 *
 * Replaces the previous one-shot "double-click to unlock" mutation that
 * called `move_vehicle_location` with no target spot. That path put the
 * ticket into `parking_lot = true` AND kept `assigned_tech_id` set,
 * violating the `tickets_parking_lot_immunity` CHECK (migration
 * 20260513120000) and crashing the unlock.
 *
 * Now the user:
 *   1. Sees customer / vehicle / job details for the ticket so they can
 *      confirm what's being checked in.
 *   2. MUST pick a specific parking spot. We deliberately do NOT offer a
 *      "general lot" choice — that's the path that triggered the
 *      constraint violation. The canonical place a vehicle lives once
 *      checked in is a real `parking_lot_spots` row.
 *   3. On Confirm, `move_vehicle_location` is called with
 *      `p_target_parking_spot_id` set, which writes:
 *          parking_spot_id = <chosen>
 *          parking_lot     = true
 *          bay_id          = NULL
 *      and the `sync_vehicle_present` trigger flips `vehicle_present =
 *      true`. `parking_lot=true` is fine here because the immunity
 *      constraint only fires when there's a tech AND no specific
 *      parking_spot would normally be considered "general lot" — the
 *      seeded demo state actually has `parking_lot=false`, but the older
 *      RPC code paths also worked through this same surface. The spot
 *      assignment is what the constraint really wants.
 *
 * Empty-state: if the shop has no parking spots configured, we show a
 * call-to-action to add them in the Facility Editor instead of letting
 * the user proceed into a broken state.
 */

type DialogJob = {
  id: string;
  name: string | null;
  jobLabel: string | null;
  minutes: number | null;
};

interface CheckInVehicleDialogProps {
  isOpen: boolean;
  onClose: () => void;

  /** The ticket being checked in. */
  ticketId: string;
  shopId: string;

  /** Read-only customer/vehicle/job context for the confirmation cards. */
  customerName?: string | null;
  customerPhone?: string | null;
  vehicleSummary?: string | null;
  vehiclePlate?: string | null;
  vehicleVin?: string | null;
  jobs?: DialogJob[];
  ticketType?: 'Waiter' | 'Drop-off' | null;
  /**
   * The ticket's scheduled start as a TIMESTAMP (the database `start_time`,
   * ISO) — never a pre-formatted display string such as the Dayboard's
   * "10:56 AM", which no Date can parse ("Invalid Date").
   */
  scheduledStartTime?: string | null;
  /** IANA zone the start is shown in (the shop's), so the day/time never shifts to the browser's zone. */
  shopTimezone?: string | null;

  /**
   * Currently-assigned tech (the one the immunity rule will null on park).
   * Used to (a) show reassurance copy in the modal ("This keeps … as the
   * scheduled technician"), (b) preserve into `scheduled_tech_id` on
   * confirm so the dispatch plan survives the parking transition.
   */
  assignedTechId?: string | null;
  assignedTechName?: string | null;

  /** Called after a successful check-in so the parent can refresh state. */
  onComplete?: () => void;
}

interface ParkingSpotOption {
  id: string;
  label: string;
  spot_type: string;
  occupied: boolean;
}

export function CheckInVehicleDialog({
  isOpen,
  onClose,
  ticketId,
  shopId,
  customerName,
  customerPhone,
  vehicleSummary,
  vehiclePlate,
  vehicleVin,
  jobs,
  ticketType,
  scheduledStartTime,
  shopTimezone,
  assignedTechId,
  assignedTechName,
  onComplete,
}: CheckInVehicleDialogProps) {
  const { toast } = useToast();
  const [spots, setSpots] = useState<ParkingSpotOption[]>([]);
  const [selectedSpotId, setSelectedSpotId] = useState<string>('');
  const [isLoadingSpots, setIsLoadingSpots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Core-100 R4 CORE-Z: false when the spots or their physical occupancy could not be read — nothing is offered.
  const [occupancyKnown, setOccupancyKnown] = useState(true);

  useEffect(() => {
    if (!isOpen || !shopId) return;

    let cancelled = false;
    const fetchSpots = async () => {
      setIsLoadingSpots(true);
      try {
        const { data: spotRows, error: spotsError } = await supabase
          .from('parking_lot_spots')
          .select('id, label, spot_type')
          .eq('shop_id', shopId)
          .eq('is_active', true)
          .order('label');

        // Core-100 R4 CORE-Z: occupancy is PHYSICAL truth (vehicle_location_current — what the move authority
        // checks under its lock), not the legacy ticket columns; a failed read is UNKNOWN and offers no spot.
        const occupied = spotsError
          ? null
          : await readShopOccupiedSpotIds(shopId).catch((e: unknown) => {
              console.warn('[CheckInVehicleDialog] spot occupancy unavailable; offering no spots:', e);
              return null;
            });

        if (cancelled) return;

        const { spots: normalized, occupancyKnown: known } = checkInSpotOptions(
          ((spotRows ?? []) as Array<{ id: string; label: string; spot_type: string | null }>).map((s) => ({
            id: s.id,
            label: s.label,
            spot_type: s.spot_type || 'Standard',
          })),
          occupied,
          ticketId,
        );

        setSpots(normalized);
        setOccupancyKnown(known && !spotsError);
        setSelectedSpotId('');
      } finally {
        if (!cancelled) setIsLoadingSpots(false);
      }
    };

    fetchSpots();
    return () => {
      cancelled = true;
    };
  }, [isOpen, shopId, ticketId]);

  // Shop-local, from a real timestamp; an absent or unparseable value is said
  // plainly instead of rendering "Invalid Date" (toLocaleString never throws).
  const formattedStartTime = useMemo(() => {
    if (!scheduledStartTime) return null;
    const at = new Date(scheduledStartTime);
    if (Number.isNaN(at.getTime())) return null;
    const zone = shopTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatInTimeZone(at, zone, 'EEE, MMM d, h:mm a');
  }, [scheduledStartTime, shopTimezone]);

  const availableSpotCount = spots.filter(s => !s.occupied).length;
  const noSpotsConfigured = !isLoadingSpots && occupancyKnown && spots.length === 0;

  const handleConfirm = async () => {
    if (!selectedSpotId) {
      toast({
        title: 'Pick a parking spot',
        description: 'Choose where the vehicle is physically parked before unlocking.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // `tickets_parking_lot_immunity` (migration 20260513120000)
      //   CHECK (NOT (parking_lot = true AND assigned_tech_id IS NOT NULL)
      //          OR status IN ('completed','Completed'))
      // requires `assigned_tech_id` to be NULL once `parking_lot` flips
      // true. The canonical `move_vehicle_location` RPC sets
      // `parking_lot=true` on the parking-spot branch but does NOT
      // touch `assigned_tech_id` — it expects the caller to have
      // unassigned the tech first.
      //
      // To avoid losing the DISPATCH PLAN through that transition, we
      // copy `assigned_tech_id → scheduled_tech_id` in the same
      // statement (migration 20260601160000 adds the column). The
      // BEFORE trigger `trg_sync_scheduled_tech_from_assigned` is a
      // no-op here because `scheduled_tech_id` is set explicitly.
      //
      // Everything else stays: `start_time`, `scheduled_start_time`,
      // `scheduled_date`, `predicted_minutes`, `type`, jobs, and timer
      // fields are all untouched. The tech reclaims active ownership
      // when the vehicle moves from parking to a bay (drag-to-bay or
      // bay-assign flow).
      // ROLLOVER CHAIN. Check-in is a PHYSICAL act (the car is parked in a
      // spot); it is not a reason to change who does a multi-day chain's
      // work, and a chain is one assignment unit. So a chain row — its head
      // or a continuation — is never unassigned here: only the location
      // moves, through the same canonical move_vehicle_location as every
      // other ticket. The lot admits it still assigned because the database
      // says so, not because this dialog does: tickets_parking_lot_immunity
      // exempts a continuation (rollover_of_ticket_id) and the head of a
      // LIVE chain (parked_with_chain_plan, computed by the server only —
      // 20270201140000). Anything else the server refuses, and its reason
      // is shown below.
      const { isRolloverChainRow } = await import('@/utils/rolloverChain');
      const chainRow = await isRolloverChainRow(ticketId);

      const update: { assigned_tech_id: null; scheduled_tech_id?: string } = {
        assigned_tech_id: null,
      };
      if (assignedTechId) {
        update.scheduled_tech_id = assignedTechId;
      }
      const { error: unassignErr } = chainRow
        ? { error: null }
        : await supabase
          .from('tickets')
          .update(update)
          .eq('id', ticketId);
      if (unassignErr) {
        console.error('[CheckInVehicleDialog] preserve-schedule + unassign error', unassignErr, {
          ticketId,
          scheduled_tech_id: assignedTechId ?? null,
        });
        toast({
          title: 'Check-in failed',
          description: `${unassignErr.message} — ticket ${ticketId}`,
          variant: 'destructive',
        });
        return;
      }

      const { data: rawData, error } = await supabase.rpc('move_vehicle_location', {
        p_ticket_id: ticketId,
        p_target_parking_spot_id: selectedSpotId,
      });

      if (error) {
        // Structured error: surface what we sent so the user can match
        // the toast against the ticket and the chosen spot without
        // hunting in logs. Useful when the failure is a constraint
        // violation (e.g. `tickets_parking_lot_immunity` for a
        // legacy-status ticket).
        const details = {
          ticket_id: ticketId,
          parking_spot_id: selectedSpotId,
          ticket_type: ticketType,
          confirmed: {
            customer: !!customerName,
            vehicle: !!vehicleSummary,
            jobs: (jobs?.length ?? 0) > 0,
          },
        };
        console.error('[CheckInVehicleDialog] move_vehicle_location error', error, details);
        toast({
          title: 'Check-in failed',
          description: `${error.message} — ticket ${ticketId}`,
          variant: 'destructive',
        });
        return;
      }

      const result = rawData as unknown as { success: boolean; reason?: string };
      if (result && !result.success) {
        toast({
          title: 'Cannot check in',
          description: result.reason || 'Spot unavailable',
          variant: 'destructive',
        });
        return;
      }

      const spotLabel = spots.find(s => s.id === selectedSpotId)?.label;
      toast({
        title: 'Vehicle checked in',
        description: spotLabel
          ? `Parked at ${spotLabel}.`
          : 'Vehicle marked present.',
      });

      onComplete?.();
      onClose();
    } catch (err: any) {
      console.error('[CheckInVehicleDialog] unexpected error', err);
      toast({
        title: 'Check-in failed',
        description: err?.message || 'Unexpected error',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirm Vehicle Check-In</DialogTitle>
          <DialogDescription>
            Verify the customer, vehicle, and job details, then choose
            where the vehicle is physically parked before unlocking.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Customer */}
          <section className="rounded-md border p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <User className="h-3.5 w-3.5" /> Customer
            </div>
            <div className="mt-1 text-sm">
              <p className="font-medium">{customerName || '—'}</p>
              {customerPhone && (
                <p className="text-xs text-muted-foreground">{customerPhone}</p>
              )}
            </div>
          </section>

          {/* Vehicle */}
          <section className="rounded-md border p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Car className="h-3.5 w-3.5" /> Vehicle
            </div>
            <div className="mt-1 text-sm">
              <p className="font-medium">{vehicleSummary || '—'}</p>
              {(vehiclePlate || vehicleVin) && (
                <p className="text-xs text-muted-foreground">
                  {vehiclePlate && <>Plate {vehiclePlate}</>}
                  {vehiclePlate && vehicleVin && <> · </>}
                  {vehicleVin && <>VIN {vehicleVin}</>}
                </p>
              )}
            </div>
          </section>

          {/* Jobs */}
          <section className="rounded-md border p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Wrench className="h-3.5 w-3.5" /> Jobs
            </div>
            {jobs && jobs.length > 0 ? (
              <ul className="mt-1 list-disc pl-5 text-sm">
                {jobs.map(j => (
                  <li key={j.id}>
                    {j.jobLabel || j.name || 'Untitled job'}
                    {typeof j.minutes === 'number' && j.minutes > 0 && (
                      <span className="text-xs text-muted-foreground"> · {j.minutes} min</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">No jobs listed.</p>
            )}
            {(ticketType || scheduledStartTime !== undefined) && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="checkin-ticket-when">
                {ticketType && <>{ticketType}</>}
                {ticketType && <> · </>}
                {formattedStartTime ?? 'No start time scheduled'}
              </p>
            )}
          </section>

          <Separator />

          {/* Location */}
          <section className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" /> Where is the vehicle parked?
            </div>
            <p className="text-xs text-muted-foreground">
              {assignedTechName
                ? <>This keeps <span className="font-medium text-foreground">{assignedTechName}</span> as the scheduled technician while the vehicle is parked. They'll reclaim active ownership when the car moves to a bay.</>
                : <>The schedule, jobs, and timing stay. A technician will pick up the work when the car moves to a bay.</>
              }
            </p>

            {!isLoadingSpots && !occupancyKnown && (
              <div className="rounded-md border border-dashed border-destructive/50 p-3 text-sm text-destructive" data-testid="checkin-occupancy-unknown">
                Spot availability could not be read, so no spot is offered — an unknown spot is not a free one. Close
                and try again.
              </div>
            )}
            {noSpotsConfigured ? (
              <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                No parking spots are configured. Add parking spots in the
                Facility Editor, then come back to check this vehicle in.
              </div>
            ) : (
              <>
                <Select
                  value={selectedSpotId}
                  onValueChange={setSelectedSpotId}
                  disabled={isLoadingSpots}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        isLoadingSpots ? 'Loading spots…' : 'Select a parking spot'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {spots.map(spot => (
                      <SelectItem
                        key={spot.id}
                        value={spot.id}
                        disabled={spot.occupied}
                      >
                        {spot.label}
                        {spot.spot_type && spot.spot_type !== 'Standard' && (
                          <> · {spot.spot_type}</>
                        )}
                        {spot.occupied && <> · occupied</>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {availableSpotCount} of {spots.length} spot
                  {spots.length === 1 ? '' : 's'} available.
                </p>
              </>
            )}
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              isSubmitting ||
              isLoadingSpots ||
              noSpotsConfigured ||
              !selectedSpotId
            }
          >
            {isSubmitting ? 'Checking in…' : 'Confirm & Unlock'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
